import { useEffect, useRef, useState } from "react";
import { Info, Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUpdationDataTypes } from "../../hooks";
import { NavigationButtons } from "../shared/NavigationButtons";
import { PageHeader } from "@shared/components/PageHeader";

// Field Card Component
const FieldCard = ({
  field,
  isSelected,
  onToggle,
  isRequired = false,
}: {
  field: { columnName: string };
  isSelected: boolean;
  onToggle: () => void;
  isRequired: boolean;
}) => {
  return (
    <div
      onClick={onToggle}
      className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3
              className={`text-sm ${
                isSelected ? "text-blue-900" : "text-gray-900"
              }`}
            >
              {field.columnName}
            </h3>
            {isRequired && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                Required
              </span>
            )}
          </div>
        </div>

        {isSelected && (
          <div className="ml-3">
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
              <Check size={16} className="text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const DataFieldUpdation = () => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const navigate = useNavigate();
  const hasLogged = useRef(false);
  const { state } = useLocation();

  const { data } = useUpdationDataTypes(state?.categoryKey);

  // Ensure fields is always an array
  const fields = Array.isArray(data) ? data : [];

  useEffect(() => {
    if (!hasLogged.current && state) {
      hasLogged.current = true;
    }

    // Only process fields if we have valid data
    if (fields.length > 0) {
      const requiredFields = fields
        .filter((field) => field?.isRequired)
        .map((field) => field.columnName)
        .filter(Boolean); // Remove any undefined/null values

      setSelectedFields(requiredFields);
    }
  }, [fields]);

  const handleFieldToggle = (fieldId: string) => {
    if (!fieldId) return;

    const field = fields.find((f) => f?.columnName === fieldId);
    const isRequired = field?.isRequired;

    // Prevent deselecting required fields
    if (isRequired && selectedFields.includes(fieldId)) return;

    setSelectedFields((prev) =>
      prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleSelectAll = () => {
    const allFieldNames = fields
      .map((field) => field?.columnName)
      .filter(Boolean); // Remove undefined/null values
    setSelectedFields(allFieldNames);
  };

  const handleClearAll = () => {
    const requiredFields = fields
      .filter((field) => field?.isRequired)
      .map((field) => field?.columnName)
      .filter(Boolean); // Remove undefined/null values
    setSelectedFields(requiredFields);
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const handleContinue = () => {
    if (!state?.dataType) {
      alert("Please select a data type before proceeding.");
      return;
    }

    if (selectedFields.length === 0) {
      alert("Please select at least one field to continue.");
      return;
    }

    const payload = {
      dataType: state.dataType,
      operation_type: state.operation_type,
      selected_fields: selectedFields,
      categoryKey: state?.categoryKey,
    };

    navigate("/onboarding/data-migration/file-upload", {
      state: payload,
    });
  };

  return (
    <div className="min-h-screen w-full">
      <div className="flex flex-col gap-5">
        {/* Header */}
        <div>
          <div className="flex items-center space-x-3">
            <p className="page-title">Data Management</p>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {state?.dataType || "Data Type"}
            </span>
          </div>
          <p className="page-subtitle">View and update your data records</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Data Migration
            </h2>
            <div className="text-sm text-blue-600">
              Step 2: Select Fields to Update - {state?.dataType || "data"}
            </div>
          </div>

          {/* Field Actions */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-medium text-gray-900">
                Select Fields to Update
              </h3>
              <Info size={16} className="text-gray-400" />
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
              >
                Select All
              </button>
              <button
                onClick={handleClearAll}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
              >
                Clear All
              </button>
              <span className="text-sm text-gray-600">
                {selectedFields.length} selected
              </span>
            </div>
          </div>

          {/* Summary */}
          {selectedFields.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <PageHeader
                    title="Field Selection Summary"
                    subtitle={`You have selected ${selectedFields.length} fields for update. Required fields cannot be deselected.`}
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedFields.map((fieldName) => {
                      const field = fields.find(
                        (f) => f?.columnName === fieldName
                      );
                      return (
                        <span
                          key={fieldName}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {fieldName}
                          {field?.isRequired ? " (Required)" : ""}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fields.map((field) => {
              if (!field?.columnName) return null; // Skip invalid fields

              return (
                <FieldCard
                  key={field.columnName}
                  field={field}
                  isSelected={selectedFields.includes(field.columnName)}
                  onToggle={() => handleFieldToggle(field.columnName)}
                  isRequired={field.isRequired || false}
                />
              );
            })}
          </div>

          {/* Navigation */}
          <NavigationButtons
            onBack={handleBack}
            onContinue={handleContinue}
            continueText="Continue"
            canContinue={selectedFields.length > 0}
          />
        </div>
      </div>
    </div>
  );
};

export default DataFieldUpdation;
